// app/src/features/tracking/useTrackingWiring.js
import { useEffect, useMemo, useRef, useState } from "react";
import { makeIsWatchlisted } from "../../lib/forecast/forecastLists.js";

/**
 * The tracking wiring's first point: the pinned (watchlisted) map, its synchronous mirror and the
 * single watchlist predicate, and the "watchlist full" popup state with its auto-hide. Called
 * where `AppInner` declared `pinned` — before the `forecasts` memo, which reads `isWatchlisted`.
 * Takes nothing: neither cell has an input.
 *
 * Not here: the tracking prefs and the settings flyover's state (`useTrackingSettingsWiring`,
 * called later, where `forecastsById` exists) and the forecast manager's `handlePin`
 * (features/layout/ForecastManagerOverlay.jsx, module 2's), which reads `pinnedRef`,
 * `WATCHLIST_LIMIT` and `setWatchlistLimitPopup` from the frame context.
 */
export function useTrackingWiring() {
  const [pinned, setPinned] = useState({});
  // Synchronous mirror of `pinned` for handlePin's watchlist-cap check. Reading
  // the render-closure `pinned` there can observe a stale value across rapid
  // pin clicks (before React commits the setPinned), which would let the
  // WATCHLIST_LIMIT cap be exceeded. This effect keeps the mirror current with
  // committed state (including the server-load merge); handlePin also updates
  // it synchronously the moment it commits a pin so back-to-back clicks count.
  // (Declared up here — the wiring is called ahead of `forecasts` in AppInner — because the
  // watchlist merge there needs the predicate this map feeds.)
  const pinnedRef = useRef(pinned);
  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);
  // Single watchlist predicate for the whole app — the Forecast Manager's two log
  // variants both consume it, so they can't drift on what "watchlisted" means.
  const isWatchlisted = useMemo(() => makeIsWatchlisted(pinned), [pinned]);
  // Single source of truth for the pin-cap check and the "watchlist full" popup copy, so they can't
  // drift.
  const WATCHLIST_LIMIT = 100;
  const [watchlistLimitPopup, setWatchlistLimitPopup] = useState(false);
  useEffect(() => {
    if (!watchlistLimitPopup) return;
    const t = setTimeout(() => setWatchlistLimitPopup(false), 2500);
    return () => clearTimeout(t);
  }, [watchlistLimitPopup]);
  return {
    pinned,
    setPinned,
    pinnedRef,
    isWatchlisted,
    WATCHLIST_LIMIT,
    watchlistLimitPopup,
    setWatchlistLimitPopup,
  };
}
